/**
 * SessionTimeoutWarningModal Component
 * 
 * Modal warning user that their session is about to expire
 * 
 * Features:
 * - Modal overlay with focus trap
 * - Warning message with time remaining
 * - Extend session option
 * - Sign out option
 * - Keyboard navigation support
 * - WCAG 2.2 AA compliance
 */

import React, { useEffect, useRef } from 'react';
import { Clock, LogOut, RefreshCw } from 'lucide-react';

interface SessionTimeoutWarningModalProps {
  isOpen: boolean;
  timeRemainingMinutes: number;
  onExtendSession: () => void | Promise<void | boolean>;
  onSignOut: () => void | Promise<void>;
}

export function SessionTimeoutWarningModal({ 
  isOpen, 
  timeRemainingMinutes,
  onExtendSession,
  onSignOut
}: SessionTimeoutWarningModalProps): React.ReactElement | null {
  const modalRef = useRef<HTMLDivElement>(null);
  const extendButtonRef = useRef<HTMLButtonElement>(null);

  // Focus trap and keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Don't allow closing via Escape - user must choose Extend or Sign Out
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Focus the extend button (primary action)
    if (extendButtonRef.current) {
      extendButtonRef.current.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const timeRemainingText = timeRemainingMinutes < 1 
    ? 'less than a minute' 
    : `${Math.floor(timeRemainingMinutes)} minute${Math.floor(timeRemainingMinutes) !== 1 ? 's' : ''}`;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-timeout-title"
      aria-describedby="session-timeout-description"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-yellow-200 px-6 py-4 dark:border-yellow-800">
            <div className="flex items-center space-x-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900">
                <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" aria-hidden="true" />
              </div>
              <div>
                <h2 id="session-timeout-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Session Expiring Soon
                </h2>
                <p id="session-timeout-description" className="text-sm text-gray-600 dark:text-gray-400">
                  Your session will expire in {timeRemainingText}
                </p>
              </div>
            </div>
            {/* Note: No close button - user must choose Extend or Sign Out */}
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <div className="rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/20">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Your session is about to expire due to inactivity. Choose an option below:
              </p>
            </div>

            <div className="mt-6 space-y-3">
              <button
                ref={extendButtonRef}
                type="button"
                onClick={onExtendSession}
                className="w-full flex items-center justify-center space-x-2 btn-primary btn-md"
              >
                <RefreshCw className="h-5 w-5" />
                <span>Extend Session</span>
              </button>
              
              <button
                type="button"
                onClick={onSignOut}
                className="w-full flex items-center justify-center space-x-2 btn-secondary btn-md"
              >
                <LogOut className="h-5 w-5" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

